// Theming for public business pages. Two independent axes:
//  1) ACCENT motif  -> buttons, prices, highlights (one cohesive color).
//  2) SURFACE motif -> page background family (dark OR light) + text/cards/inputs.
// Both inject CSS variables on the page wrapper (and, for portaled dialogs,
// directly on the dialog content). See design_guidelines.json.

// --------------------------------------------------------------- Accent colors
export const MOTIFS = [
  { id: 'amber_warmth',  name_lv: 'Dzintara siltums', accent: '#FF9F1C', hover: '#E58E17', contrast: '#000000', soft: 'rgba(255,159,28,0.12)',  ring: 'rgba(255,159,28,0.35)',  border: 'rgba(255,159,28,0.40)' },
  { id: 'nordic_teal',   name_lv: 'Ziemeļu tirkīzs',  accent: '#2EC4B6', hover: '#26AB9E', contrast: '#000000', soft: 'rgba(46,196,182,0.12)',  ring: 'rgba(46,196,182,0.35)',  border: 'rgba(46,196,182,0.40)' },
  { id: 'royal_violet',  name_lv: 'Karaliskais violets', accent: '#8B5CF6', hover: '#7C3AED', contrast: '#FFFFFF', soft: 'rgba(139,92,246,0.15)', ring: 'rgba(139,92,246,0.35)', border: 'rgba(139,92,246,0.40)' },
  { id: 'emerald_glow',  name_lv: 'Smaragda spīdums', accent: '#10B981', hover: '#059669', contrast: '#000000', soft: 'rgba(16,185,129,0.12)',  ring: 'rgba(16,185,129,0.35)',  border: 'rgba(16,185,129,0.40)' },
  { id: 'crimson_spark', name_lv: 'Rubīna liesma',    accent: '#EF4444', hover: '#DC2626', contrast: '#FFFFFF', soft: 'rgba(239,68,68,0.15)',   ring: 'rgba(239,68,68,0.35)',   border: 'rgba(239,68,68,0.40)' },
  { id: 'cyber_cyan',    name_lv: 'Kiberciāns',       accent: '#06B6D4', hover: '#0891B2', contrast: '#000000', soft: 'rgba(6,182,212,0.12)',   ring: 'rgba(6,182,212,0.35)',   border: 'rgba(6,182,212,0.40)' },
  { id: 'sunburst_gold', name_lv: 'Saulrieta zelts',  accent: '#F59E0B', hover: '#D97706', contrast: '#000000', soft: 'rgba(245,158,11,0.12)',  ring: 'rgba(245,158,11,0.35)',  border: 'rgba(245,158,11,0.40)' },
  { id: 'berry_rose',    name_lv: 'Ogu rozā',         accent: '#EC4899', hover: '#DB2777', contrast: '#FFFFFF', soft: 'rgba(236,72,153,0.15)',  ring: 'rgba(236,72,153,0.35)',  border: 'rgba(236,72,153,0.40)' },
];
export const ACCENT_MOTIFS = MOTIFS;
export const MOTIF_BY_ID = Object.fromEntries(MOTIFS.map((m) => [m.id, m]));

// Per-template default accent (keeps historical look until an owner picks one).
export const DEFAULT_MOTIF = {
  menu: 'nordic_teal',
  reservations: 'amber_warmth',
  business_card: 'royal_violet',
};

// Returns the accent CSS-variable style object for a motif id.
export function motifVars(id, template = 'reservations') {
  const m = MOTIF_BY_ID[id] || MOTIF_BY_ID[DEFAULT_MOTIF[template]] || MOTIF_BY_ID.amber_warmth;
  return {
    '--accent': m.accent,
    '--accent-hover': m.hover,
    '--accent-contrast': m.contrast,
    '--accent-soft': m.soft,
    '--accent-ring': m.ring,
    '--accent-border': m.border,
  };
}

// ------------------------------------------------------------- Surface (fons)
// Each surface provides a full palette so pages, cards, inputs, dialogs and
// text all stay readable in both light and dark families.
export const SURFACE_MOTIFS = [
  // --- Tumšie (dark) ---
  { id: 'melnais', name_lv: 'Melnais', family: 'dark', is_default: true, swatch: '#0A0A0A', vars: {
    '--bg-page': '#0A0A0A', '--bg-surface-secondary': '#0F0F0F', '--bg-card': 'rgba(255,255,255,0.05)',
    '--border-card': 'rgba(255,255,255,0.10)', '--border-divider': 'rgba(255,255,255,0.08)',
    '--text-primary': '#FFFFFF', '--text-muted': 'rgba(255,255,255,0.65)', '--text-faint': 'rgba(255,255,255,0.40)',
    '--bg-input': 'rgba(255,255,255,0.06)', '--border-input': 'rgba(255,255,255,0.15)',
    '--bg-header': 'rgba(10,10,10,0.85)', '--bg-overlay': 'rgba(0,0,0,0.75)' } },
  { id: 'nakts_zils', name_lv: 'Nakts zils', family: 'dark', swatch: '#0B132B', vars: {
    '--bg-page': '#0B132B', '--bg-surface-secondary': '#1C2541', '--bg-card': 'rgba(255,255,255,0.06)',
    '--border-card': 'rgba(255,255,255,0.12)', '--border-divider': 'rgba(255,255,255,0.08)',
    '--text-primary': '#F8FAFC', '--text-muted': '#94A3B8', '--text-faint': '#64748B',
    '--bg-input': 'rgba(255,255,255,0.07)', '--border-input': 'rgba(255,255,255,0.18)',
    '--bg-header': 'rgba(11,19,43,0.88)', '--bg-overlay': 'rgba(5,10,24,0.80)' } },
  { id: 'terauda_ogle', name_lv: 'Tērauda ogle', family: 'dark', swatch: '#121417', vars: {
    '--bg-page': '#121417', '--bg-surface-secondary': '#1B1E23', '--bg-card': 'rgba(255,255,255,0.05)',
    '--border-card': 'rgba(255,255,255,0.11)', '--border-divider': 'rgba(255,255,255,0.07)',
    '--text-primary': '#F3F4F6', '--text-muted': '#9CA3AF', '--text-faint': '#6B7280',
    '--bg-input': 'rgba(255,255,255,0.06)', '--border-input': 'rgba(255,255,255,0.16)',
    '--bg-header': 'rgba(18,20,23,0.88)', '--bg-overlay': 'rgba(10,11,14,0.78)' } },
  { id: 'dzilais_mezs', name_lv: 'Dziļais mežs', family: 'dark', swatch: '#0B1612', vars: {
    '--bg-page': '#0B1612', '--bg-surface-secondary': '#13231D', '--bg-card': 'rgba(255,255,255,0.05)',
    '--border-card': 'rgba(255,255,255,0.11)', '--border-divider': 'rgba(255,255,255,0.07)',
    '--text-primary': '#F0FDF4', '--text-muted': '#A7F3D0', '--text-faint': '#5E8171',
    '--bg-input': 'rgba(255,255,255,0.06)', '--border-input': 'rgba(255,255,255,0.15)',
    '--bg-header': 'rgba(11,22,18,0.88)', '--bg-overlay': 'rgba(5,12,9,0.80)' } },
  // --- Gaišie (light) ---
  { id: 'gaisais', name_lv: 'Gaišais', family: 'light', swatch: '#FFFFFF', vars: {
    '--bg-page': '#FFFFFF', '--bg-surface-secondary': '#F8FAFC', '--bg-card': '#FFFFFF',
    '--border-card': '#E2E8F0', '--border-divider': '#F1F5F9',
    '--text-primary': '#0F172A', '--text-muted': '#475569', '--text-faint': '#94A3B8',
    '--bg-input': '#F8FAFC', '--border-input': '#CBD5E1',
    '--bg-header': 'rgba(255,255,255,0.90)', '--bg-overlay': 'rgba(15,23,42,0.50)' } },
  { id: 'mikstais_peleks', name_lv: 'Mīkstais pelēks', family: 'light', swatch: '#F3F4F6', vars: {
    '--bg-page': '#F3F4F6', '--bg-surface-secondary': '#FFFFFF', '--bg-card': '#FFFFFF',
    '--border-card': '#E5E7EB', '--border-divider': '#E5E7EB',
    '--text-primary': '#111827', '--text-muted': '#4B5563', '--text-faint': '#9CA3AF',
    '--bg-input': '#FFFFFF', '--border-input': '#D1D5DB',
    '--bg-header': 'rgba(243,244,246,0.90)', '--bg-overlay': 'rgba(17,24,39,0.50)' } },
  { id: 'kremkrasa', name_lv: 'Krēmkrāsa', family: 'light', swatch: '#FAF7F2', vars: {
    '--bg-page': '#FAF7F2', '--bg-surface-secondary': '#FFFDF9', '--bg-card': '#FFFDF9',
    '--border-card': '#ECE5D8', '--border-divider': '#F2EBE0',
    '--text-primary': '#2C221E', '--text-muted': '#685952', '--text-faint': '#A3958C',
    '--bg-input': '#FFFDF9', '--border-input': '#DFD6C8',
    '--bg-header': 'rgba(250,247,242,0.90)', '--bg-overlay': 'rgba(44,34,30,0.50)' } },
  { id: 'salvijas_zala', name_lv: 'Salvijas zaļais', family: 'light', swatch: '#F1F5F3', vars: {
    '--bg-page': '#F1F5F3', '--bg-surface-secondary': '#F8FAFC', '--bg-card': '#FFFFFF',
    '--border-card': '#D5E0DC', '--border-divider': '#E2EBE7',
    '--text-primary': '#182622', '--text-muted': '#4B615B', '--text-faint': '#8DA19B',
    '--bg-input': '#FFFFFF', '--border-input': '#C5D5CF',
    '--bg-header': 'rgba(241,245,243,0.90)', '--bg-overlay': 'rgba(24,38,34,0.50)' } },
];
export const SURFACE_BY_ID = Object.fromEntries(SURFACE_MOTIFS.map((s) => [s.id, s]));
export const DEFAULT_SURFACE = 'melnais';

function resolveSurface(id) {
  return SURFACE_BY_ID[id] || SURFACE_BY_ID[DEFAULT_SURFACE];
}

// Returns the surface CSS-variable style object for a surface id.
export function surfaceVars(id) {
  return { ...resolveSurface(id).vars };
}

export function isLightSurface(id) {
  return resolveSurface(id).family === 'light';
}

// Combined accent + surface vars for a page/dialog wrapper.
export function pageVars(surfaceId, accentId, template = 'reservations') {
  return { ...surfaceVars(surfaceId), ...motifVars(accentId, template) };
}
